import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

class ClassInfoService {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  async getClassInfo(classId) {
    if (this.cache.has(classId)) {
      return this.cache.get(classId);
    }

    if (this.loadingPromises.has(classId)) {
      return this.loadingPromises.get(classId);
    }

    const loadingPromise = this.loadClassInfo(classId);
    this.loadingPromises.set(classId, loadingPromise);

    try {
      const classInfo = await loadingPromise;
      this.cache.set(classId, classInfo);
      return classInfo;
    } finally {
      this.loadingPromises.delete(classId);
    }
  }

  async loadClassInfo(classId) {
    try {
      const classDoc = await getDoc(doc(db, 'classes', classId));
      if (classDoc.exists()) {
        const classData = classDoc.data();
        const subjectTeacherAssignments = classData.subjectTeacherAssignments || {};

        // Resolve homeroom teacher name
        let homeroomTeacherName = 'N/A';
        if (classData.teacherId) {
          try {
            const teacherDoc = await getDoc(doc(db, 'users', classData.teacherId));
            if (teacherDoc.exists()) {
              homeroomTeacherName = teacherDoc.data().username || 'N/A';
            }
          } catch (e) {
            console.error('ClassInfoService: failed to load homeroom teacher', e.message);
          }
        }

        // Resolve subject teacher names
        const subjectTeachers = [];
        for (const [subject, teacherId] of Object.entries(subjectTeacherAssignments)) {
          let teacherName = 'N/A';
          try {
            const teacherDoc = await getDoc(doc(db, 'users', teacherId));
            if (teacherDoc.exists()) {
              teacherName = teacherDoc.data().username || 'N/A';
            }
          } catch (e) {
            console.error('ClassInfoService: failed to load subject teacher for', subject, e.message);
          }
          subjectTeachers.push({ subject, teacherId, teacherName });
        }

        return {
          id: classId,
          name: classData.name,
          grade: classData.grade,
          gradeId: classData.gradeId || null,
          academicYearId: classData.academicYearId || null,
          schoolId: classData.schoolId || null,
          teacherId: classData.teacherId,
          teacherName: homeroomTeacherName,
          subjectTeachers,
          requiredSubjects: classData.requiredSubjects || [],
          source: 'classes-collection'
        };
      }
    } catch (error) {
      console.error('ClassInfoService: failed to load class', classId, error.message);
    }

    return {
      id: classId,
      name: 'Class Not Found',
      grade: 'N/A',
      teacherId: null,
      teacherName: 'N/A',
      subjectTeachers: [],
      requiredSubjects: [],
      source: 'not-found'
    };
  }

  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  getCacheStats() {
    return {
      cached: this.cache.size,
      loading: this.loadingPromises.size
    };
  }
}

// Export singleton instance
export const classInfoService = new ClassInfoService();
